import { SubscriptionLevel } from "../autogenerated/models";

/**
 * unSHACLed capability matrix — must mirror SubscriptionLevelExtensions on the backend.
 *
 * Trial:      validation + preprocessing
 * Generation: schema-driven + business-driven generation
 * Validation: validation + preprocessing
 * Full:       everything
 * CIMbion tiers (None/Unpaid/Trial/Basic/Incremental/Advanced) have NO unSHACLed access.
 */
const UNSHACLED_VALIDATION = new Set<SubscriptionLevel>([
    SubscriptionLevel.UnshacledTrial,
    SubscriptionLevel.UnshacledValidation,
    SubscriptionLevel.UnshacledFull,
]);

const UNSHACLED_GENERATION = new Set<SubscriptionLevel>([
    SubscriptionLevel.UnshacledGeneration,
    SubscriptionLevel.UnshacledFull,
]);

const UNSHACLED_PREPROCESSING = new Set<SubscriptionLevel>([
    SubscriptionLevel.UnshacledTrial,
    SubscriptionLevel.UnshacledValidation,
    SubscriptionLevel.UnshacledFull,
]);

const has = (set: Set<SubscriptionLevel>, s: SubscriptionLevel | undefined) =>
    s !== undefined && set.has(s);

export const canRunValidation = (s: SubscriptionLevel | undefined) =>
    has(UNSHACLED_VALIDATION, s);

export const canGenerateSchemaDriven = (s: SubscriptionLevel | undefined) =>
    has(UNSHACLED_GENERATION, s);

export const canGenerateBusinessDriven = (s: SubscriptionLevel | undefined) =>
    has(UNSHACLED_GENERATION, s);

export const canUsePreprocessing = (s: SubscriptionLevel | undefined) =>
    has(UNSHACLED_PREPROCESSING, s);

export const hasAnyUnshacledAccess = (s: SubscriptionLevel | undefined) =>
    canRunValidation(s) || canGenerateSchemaDriven(s) || canGenerateBusinessDriven(s);

/**
 * CAS tiers that can create conformance test sessions — mirrors HasCasAccess on the backend.
 * CAS and unSHACLed are bought separately, so an unSHACLed-only user has no CAS tier (None).
 */
const CAS_ACCESS = new Set<SubscriptionLevel>([
    SubscriptionLevel.Trial,
    SubscriptionLevel.Basic,
    SubscriptionLevel.Incremental,
    SubscriptionLevel.Advanced,
]);

export const hasCasAccess = (s: SubscriptionLevel | undefined) => has(CAS_ACCESS, s);

const SUBSCRIPTION_LABELS: Record<SubscriptionLevel, string> = {
    [SubscriptionLevel.None]: "No subscription",
    [SubscriptionLevel.Unpaid]: "CGMES CT Unpaid",
    [SubscriptionLevel.Trial]: "CGMES CT Trial",
    [SubscriptionLevel.Basic]: "CGMES CT Basic",
    [SubscriptionLevel.Incremental]: "CGMES CT Incremental",
    [SubscriptionLevel.Advanced]: "CGMES CT Advanced",
    [SubscriptionLevel.UnshacledTrial]: "unSHACLed Trial",
    [SubscriptionLevel.UnshacledGeneration]: "unSHACLed Generation",
    [SubscriptionLevel.UnshacledValidation]: "unSHACLed Validation",
    [SubscriptionLevel.UnshacledFull]: "unSHACLed Full",
};

export const subscriptionLabel = (s: SubscriptionLevel) =>
    SUBSCRIPTION_LABELS[s] ?? SubscriptionLevel[s];

/** All enum values, in declaration order — drives admin pickers & badges. */
export const ALL_SUBSCRIPTIONS: SubscriptionLevel[] = Object.values(SubscriptionLevel)
    .filter((v): v is SubscriptionLevel => typeof v === "number");

export const isUnshacledSubscription = (s: SubscriptionLevel) =>
    s === SubscriptionLevel.UnshacledTrial ||
    s === SubscriptionLevel.UnshacledGeneration ||
    s === SubscriptionLevel.UnshacledValidation ||
    s === SubscriptionLevel.UnshacledFull;

export const CIMBION_SUBSCRIPTIONS: SubscriptionLevel[] = ALL_SUBSCRIPTIONS.filter(
    (s) => !isUnshacledSubscription(s)
);

export const UNSHACLED_SUBSCRIPTIONS: SubscriptionLevel[] = [
    SubscriptionLevel.None,
    ...ALL_SUBSCRIPTIONS.filter((s) => isUnshacledSubscription(s)),
];
